import { SvgIcon, useIsWindowBiggerThan, useStickyBarHeight } from '@bob/library'
import { AppProviderContext } from '@websrc/App/AppContext'
import { matomo } from '@websrc/App/Tracking/Matomo'
import { mapComparisonTable } from '@websrc/DataMappers/mapSpot'
import React, { useContext, useEffect, useRef, useState } from 'react'

import { Button, BUTTON_WIDTH } from '../Button/Button'
import { ProductInfoButtons } from '../Details/ProductInfo/Buttons/ProductInfoButtons'
import { Markdown } from '../Markdown/Markdown'
import { table } from './ComparisonTable.css'
import { ComparisonTableNotStickyThead } from './ComparisonTableNotStickyThead'
import { ComparisonTableStickyThead } from './ComparisonTableStickyThead'
import { isCellVisible } from './ComparisonTableUtils'

interface ComparisonTableProps {
  comparisonTable: ReturnType<typeof mapComparisonTable>
}
// eslint-disable-next-line max-lines-per-function
function ComparisonTable({ comparisonTable }: ComparisonTableProps) {
  const stickyBarHeight = useStickyBarHeight()
  const [visibleProductIndex, setVisibleProductIndex] = useState(0)
  const [isSticky, setIsSticky] = useState(false)
  const isMobile = !useIsWindowBiggerThan(500)
  const isTablet = !useIsWindowBiggerThan(1023)

  const stickyThresholdRef = useRef<HTMLDivElement>(null)

  const tableRef = useRef<HTMLTableElement>(null)
  const theadRef = useRef<HTMLTableSectionElement>(null)
  const { matomoPageTemplate } = useContext(AppProviderContext)

  useEffect(() => {
    let debounce: ReturnType<typeof setTimeout>
    const handleResize = () => {
      clearTimeout(debounce)
      debounce = setTimeout(() => {
        if (tableRef.current && theadRef.current) {
          tableRef.current.style.marginTop = `-${theadRef.current.offsetHeight}px`
        }
      }, 200)
    }

    window.addEventListener('resize', handleResize)
    handleResize()

    return () => {
      clearTimeout(debounce)
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  useEffect(() => {
    const stickyThreshold = stickyThresholdRef.current
    if (stickyThreshold) {
      const observer = new IntersectionObserver(
        ([e]) => {
          if (e.intersectionRatio === 0) {
            setIsSticky(true)
          } else if (e.intersectionRatio > 0) {
            setIsSticky(false)
          }
        },
        { threshold: [0, 1], rootMargin: `${-stickyBarHeight + 50}px 0px 200px 0px` }
      )

      observer.observe(stickyThreshold)
      return () => {
        observer.unobserve(stickyThreshold)
      }
    }
  }, [stickyBarHeight])

  return (
    <>
      <div className={table.stickyMarker} ref={stickyThresholdRef}>
        &nbsp;
      </div>
      <table className={table._} ref={tableRef}>
        <ComparisonTableStickyThead
          isMobile={isMobile}
          isSticky={isSticky}
          isTablet={isTablet}
          products={comparisonTable.products}
          ref={theadRef}
          setVisibleProductIndex={setVisibleProductIndex}
          stickyBarHeight={stickyBarHeight}
          visibleProductIndex={visibleProductIndex}
        />
        <ComparisonTableNotStickyThead
          isMobile={isMobile}
          products={comparisonTable.products}
          setVisibleProductIndex={setVisibleProductIndex}
          visibleProductIndex={visibleProductIndex}
        />
        <tbody>
          {comparisonTable.feature_row.map((row, i) => (
            <tr className={table.tbody.tr} key={i}>
              <th className={table.tbody.th}>
                <div className={table.feature.title}>{row.feature_row_title}</div>
                <div className={table.feature.description}>{row.feature_row_description}</div>
              </th>

              {row.feature_row_item.map(
                (item, k) =>
                  isCellVisible(k, visibleProductIndex, isMobile) && (
                    <td className={table.td} key={k}>
                      {!!item.feature_row_item_availability?.length &&
                        (item.feature_row_item_availability[0].feature_availability ===
                        'Available' ? (
                          <SvgIcon name={'CLOSE'} size={isTablet ? 18 : 24} strokeWidth={4} />
                        ) : (
                          <SvgIcon name={'CHECKMARK'} size={isTablet ? 18 : 24} strokeWidth={4} />
                        ))}
                      {!!item.feature_row_item_markdown && (
                        <Markdown markdown={item.feature_row_item_markdown} />
                      )}
                      {!!item.feature_row_item_cta?.text && (
                        <div>
                          <Button
                            color={'blue'}
                            colorMode={'light'}
                            href={
                              item.feature_row_item_cta.url
                                ? item.feature_row_item_cta.url
                                : undefined
                            }
                            iconName={'ARROW_RIGHT'}
                            iconPosition={'left'}
                            onClick={() => {
                              matomo.trackEvent(
                                'engagement',
                                matomoPageTemplate,
                                ['comparison table'],
                                [
                                  'feature row: ' +
                                    row.feature_row_title +
                                    ', product: ' +
                                    comparisonTable.products[k].name,
                                ],
                                'link'
                              )
                            }}
                            removePadding={true}
                            size={isTablet ? 'small' : 'medium'}
                            target={'_blank'}
                            text={item.feature_row_item_cta?.text}
                            type={'tertiary'}
                            width={BUTTON_WIDTH.Auto}
                          />
                        </div>
                      )}
                    </td>
                  )
              )}
            </tr>
          ))}

          <tr>
            <td> </td>
            {comparisonTable?.products.map(
              (item, i) =>
                isCellVisible(i, visibleProductIndex, isMobile) && (
                  <td className={table.td} key={i}>
                    <ProductInfoButtons
                      color={'blue'}
                      colorMode={'light'}
                      fontSize={isTablet ? 'sm' : 'md'}
                      productDetails={item}
                      quantity={1}
                      size={'medium'}
                      type={'secondary'}
                    />
                  </td>
                )
            )}
          </tr>
        </tbody>
      </table>
    </>
  )
}

export default ComparisonTable
